import { useQuery } from '@tanstack/react-query';
import { startOfDay, addDays } from 'date-fns';
import { useWorkspaces } from './useWorkspaces';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/lib/query-keys';

export type ActivityView = 'missed' | 'today' | 'upcoming';

export type ActivityType = 'in_person' | 'phone_call' | 'video_call' | 'email' | 'sms';

export interface ActivityWithContact {
  id: string;
  contact_id: string;
  workspace_id: string;
  user_id: string;
  type: ActivityType | null;
  title: string;
  description: string | null;
  start_date: string;
  end_date: string | null;
  completed: boolean;
  scheduled: boolean;
  calendar_event_id: string | null;
  contact_first_name: string | null;
  contact_last_name: string | null;
}

interface UseWorkspaceActivitiesParams {
  view: ActivityView;
  page?: number;
  pageSize?: number;
}

interface UseWorkspaceActivitiesResult {
  activities: ActivityWithContact[];
  totalCount: number;
  isLoading: boolean;
  error: Error | null;
}

function getDateBoundaries() {
  const now = new Date();
  return {
    todayStart: startOfDay(now).toISOString(),
    tomorrowStart: startOfDay(addDays(now, 1)).toISOString(),
  };
}

export function useWorkspaceActivities({
  view,
  page = 0,
  pageSize = 25,
}: UseWorkspaceActivitiesParams): UseWorkspaceActivitiesResult {
  const { currentWorkspace } = useWorkspaces();
  const workspaceId = currentWorkspace?.id;

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.activities.list(workspaceId, view, page),
    queryFn: async () => {
      if (!workspaceId) return { activities: [], totalCount: 0 };

      const { todayStart, tomorrowStart } = getDateBoundaries();
      const rangeStart = page * pageSize;

      // Calendar views surface outstanding **scheduled** tasks only; after-
      // the-fact logged activities (scheduled=false) live in contact history
      // and are not "to-do" items even when `completed` is still false.
      let query = supabase
        .from('activities')
        .select(
          `
          id,
          contact_id,
          workspace_id,
          user_id,
          type,
          title,
          description,
          start_date,
          end_date,
          completed,
          scheduled,
          calendar_event_id,
          contacts!inner(first_name, last_name)
        `,
          { count: 'exact' }
        )
        .eq('workspace_id', workspaceId)
        .eq('scheduled', true)
        .eq('completed', false);

      // Apply view-specific filters
      switch (view) {
        case 'missed':
          query = query
            .lt('start_date', todayStart)
            .order('start_date', { ascending: false });
          break;
        case 'today':
          query = query
            .gte('start_date', todayStart)
            .lt('start_date', tomorrowStart)
            .order('start_date', { ascending: true });
          break;
        case 'upcoming':
          query = query
            .gte('start_date', tomorrowStart)
            .order('start_date', { ascending: true });
          break;
      }

      // Apply pagination
      query = query.range(rangeStart, rangeStart + pageSize - 1);

      const { data: rows, error: queryError, count } = await query;

      if (queryError) throw queryError;

      // Flatten the joined contact data
      const activities: ActivityWithContact[] = (rows ?? []).map((row: any) => ({
        id: row.id,
        contact_id: row.contact_id,
        workspace_id: row.workspace_id,
        user_id: row.user_id,
        type: row.type,
        title: row.title,
        description: row.description,
        start_date: row.start_date,
        end_date: row.end_date,
        completed: row.completed,
        scheduled: row.scheduled,
        calendar_event_id: row.calendar_event_id ?? null,
        contact_first_name: row.contacts?.first_name ?? null,
        contact_last_name: row.contacts?.last_name ?? null,
      }));

      return { activities, totalCount: count ?? 0 };
    },
    enabled: !!workspaceId,
    staleTime: 30 * 1000,
  });

  return {
    activities: data?.activities ?? [],
    totalCount: data?.totalCount ?? 0,
    isLoading,
    error: error as Error | null,
  };
}
